import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { makeStyles, fade, CircularProgress } from "@material-ui/core";
import ShipmentViewModel from "../../viewmodels/ShipmentViewModel";
import TrackingTable from "../components/TableMaterial/TrackingTable";
import * as Utils from "./../../commons/Utils";
import { ExpeditionStatusResponse, PageableShipment, Shipment } from "../../client";
import SessionStorage from "../../data/sessionStorage/SessionStorage";
import UsersViewModel from "../../viewmodels/UsersViewModel";

export interface IAdminTrackingTableProps {
  handleSelectedRef: (ref: Shipment) => void;
  classes: any;
  selectedRef: number | undefined;
  selectedIndex: number | undefined;
  filterCheckHideAnswered: boolean;
  filterCheckOrderByLastMsg: boolean;
}

const AdminTrackingTable: React.FunctionComponent<IAdminTrackingTableProps> = (props: IAdminTrackingTableProps) => {
  const { t } = useTranslation();
  const vmShipment = ShipmentViewModel.getInstance();
  const vmUser = UsersViewModel.getInstance();
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [totalPages, setTotalPages] = React.useState<number>(50);
  const [columns, setColumns] = React.useState<any[]>([]);
  //const [isLoading, setIsLoading] = React.useState<boolean>(false)

  const handleCurrentPage = (currentPage: number) => {
    setCurrentPage(currentPage + 1);
    vmShipment.setSelectedRowIndex(undefined);
    vmShipment.unLoadShipment();
    vmShipment.unloadAdminShipments();
    vmShipment.setIsLoadingInTable(true);

    if (props.filterCheckOrderByLastMsg) {
      vmShipment.filterExpeditionsByLastMessage(currentPage + 1, 50).then((response) => {
        setCurrentPage(response.currentPage ?? 0);
        setTotalPages(response.totalPages ?? 0);
        vmShipment.setIsLoadingInTable(false);
      });
    } else {
      vmShipment.loadAdminShipments(currentPage + 1, 50).then((pageableShipments: PageableShipment) => {
        setCurrentPage(pageableShipments.currentPage || 0);
        setTotalPages(pageableShipments.totalPages || 0);
        vmShipment.setIsLoadingInTable(false);
      });
    }
  };

  const handleRowsSelected = (rows: any) => {
    ///debugger
    //setRowsSelectedByUser(rows)
    if (!vmShipment.loadingShipment) {
      props.handleSelectedRef(rows[0]);
    }
  };

  React.useEffect(() => {
    if (!vmShipment.shipmentsAdmin) {
      vmShipment.isLoadingInTable = true;
      vmShipment
        .loadAdminShipments()
        .then((pageableShipments: PageableShipment) => {
          setCurrentPage(pageableShipments.currentPage || 1);
          setTotalPages(pageableShipments.totalPages || 1);
        })
        .finally(() => (vmShipment.isLoadingInTable = false));
    }
  }, []);

  useEffect(() => {
    // Skip the first render when currentPage is 0 and shipmentsAdmin haven't been loaded yet
    if (currentPage === 0 && vmShipment.shipmentsAdmin) {
      return;
    }

    vmShipment.setIsLoadingInTable(true);
    if (props.filterCheckOrderByLastMsg) {
      vmShipment
        .filterExpeditionsByLastMessage(currentPage, 50, props.filterCheckHideAnswered)
        .then((pageableShipments: PageableShipment) => {
          vmShipment.setIsLoadingInTable(false);
          setCurrentPage(pageableShipments.currentPage || 0);
          setTotalPages(pageableShipments.totalPages || 0);
        });
    } else {
      vmShipment
        .loadAdminShipments(currentPage, 50, props.filterCheckHideAnswered)
        .then((res) => vmShipment.setIsLoadingInTable(false));
    }
  }, [props.filterCheckOrderByLastMsg, props.filterCheckHideAnswered, currentPage]);

  React.useEffect(() => {
    if (vmShipment.shipmentsAdmin) {
      const renderedColumns: any[] = [
        {
          width: 20,
          label: t("trackingAdminView.table.ref"),
          name: "shipmentExp",
          options: {
            customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
              return (
                <span
                  style={{
                    ...(vmShipment.shipmentsAdmin?.[tableMeta.rowIndex]?.unAnswered ? { fontWeight: "bold" } : {}),
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {value}
                </span>
              );
            },
          },
        },
        {
          width: 40,
          label: t("trackingAdminView.table.date"),
          name: "date",
          options: {
            customBodyRenderLite: (dataIndex: any) => {
              let val = Utils.timestamp2HumanReadableDate(vmShipment.shipmentsAdmin?.[dataIndex]?.creationDate!.getTime()!, t);
              return val;
            },
          },
        },
      ];

      if (vmUser.currentUser?.role?.toLocaleLowerCase() === "admin") {
        renderedColumns.push({
          width: 40,
          label: t("trackingAdminView.table.manager"),
          name: "accountManagerId",
          options: {
            customBodyRender: (value: any) => {
              return <span>{value}</span>;
            },
          },
        });
      }

      setColumns(renderedColumns);
    }
  }, [vmShipment.shipmentsAdmin]);

  const searchExpedition = (expNumber: string) => {
    console.log("Searching expedition with expNumber:", expNumber);
    vmShipment.isLoadingInTable = true;
    vmShipment.unLoadShipment();
    vmShipment.searchAdminShipment(expNumber as unknown as number).finally(() => (vmShipment.isLoadingInTable = false));
  };

  return (
    <>
      <div>
        <div>
          {vmShipment.shipmentsAdmin && vmShipment.shipmentsAdmin.length === 0 ? (
            /* Circular progress when table is not loaded */
            <div>
              <CircularProgress className={props.classes.circleProgress} />
            </div>
          ) : (
            <div>
              <TrackingTable
                searchExpedition={searchExpedition}
                data={vmShipment.shipmentsAdmin || []}
                loadShipments={vmShipment.loadAdminShipments}
                setIsLoadingInTable={vmShipment.setIsLoadingInTable}
                selectableRows="single"
                selectableRowsOnClick={true}
                handleRowsSelected={handleRowsSelected}
                handleCurrentPage={handleCurrentPage}
                updatePage={false}
                page={currentPage - 1}
                count={totalPages * 50}
                isLoading={vmShipment.isLoadingInTable}
                selectedRef={props.selectedRef}
                selectedIndex={props.selectedIndex}
                columns={columns}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default observer(AdminTrackingTable);
